import React, { useState, useEffect } from "react";
import CircularProgressBar from "../components/CircularProgressBar";
import VerticalProgressBar from "../components/VerticalProgressBar";
import GiftModal from "../components/GiftModal";
import PricingModal from "../components/PricingModal";
import AppBar from '../components/AppBar';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Users,
  Award,
  TrendingUp,
  Gift,
  Sparkles,
  User,
  CreditCard,
  X,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { MAIN_BG_GRADIENT } from '../utils/constants';

export default function ReferralDashboard() {
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [userData, setUserData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingProfile, setUpdatingProfile] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [error, setError] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [reactivatingSubscription, setReactivatingSubscription] = useState(false);
  const [upgradingSubscription, setUpgradingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to log out');
    }
  };

  // Fetch user data and tasks
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentUser) return;
        
        // Fetch user data
        const userData = await userService.getUserData(currentUser.uid);
        setUserData(userData);

        // Fetch tasks
        try {
          const tasksData = await userService.getUserTasks(currentUser.uid);
          setTasks(tasksData);
        } catch (error) {
          // If no tasks exist, initialize them
          const initData = await userService.initializeTasks(currentUser.uid);
          setTasks(initData);
          toast.success('Tasks initialized successfully');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.status === 500) {
          setError('session_expired');
        } else {
          setError('general');
        }
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  // Add mouse glow effect
  useEffect(() => {
    const glow = document.getElementById("mouse-glow");
    const update = (e) => {
      glow.style.transform = `translate(${e.clientX - 150}px, ${e.clientY - 150}px)`;
    };
    document.addEventListener("mousemove", update);
    return () => document.removeEventListener("mousemove", update);
  }, []);

  // Handle task completion
  const handleCompleteTask = async (taskId) => {
    try {
      setCompletingTaskId(taskId);
      const result = await userService.completeTask(currentUser.uid, taskId);
      
      // Update tasks list
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, completed: true, completed_at: new Date().toISOString() }
          : task
      ));

      // Update user data with new score
      setUserData(prev => ({
        ...prev,
        score: prev.score + result.points_earned
      }));

      toast.success(`Task completed! Earned ${result.points_earned} points`);
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(error.message);
    } finally {
      setCompletingTaskId(null);
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setUpdatingProfile(true);
      const formData = new FormData(e.target);
      const bio = formData.get('bio');
      
      const updatedUserData = await userService.updateProfile(currentUser.uid, bio);
      setUserData(updatedUserData);
      toast.success('Bio updated successfully');
    } catch (error) {
      console.error('Error updating bio:', error);
      toast.error(error.message);
    } finally {
      setUpdatingProfile(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async (cancelAtPeriodEnd = true) => {
    try {
      setCancellingSubscription(true);
      const result = await userService.cancelSubscription(currentUser.uid, cancelAtPeriodEnd);
      
      // Update user data with new subscription status
      setUserData(prev => ({
        ...prev,
        subscription_status: result.subscription_status,
        tier: cancelAtPeriodEnd ? prev.tier : 'free'
      }));

      toast.success(result.message);
      setShowCancelConfirm(false);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.message);
    } finally {
      setCancellingSubscription(false);
    }
  };

  // Handle subscription reactivation
  const handleReactivateSubscription = async () => {
    try {
      setReactivatingSubscription(true);
      const result = await userService.reactivateSubscription(currentUser.uid);
      
      // Update user data with new subscription status
      setUserData(prev => ({
        ...prev,
        subscription_status: result.subscription_status
      }));

      toast.success(result.message);
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error(error.message);
    } finally {
      setReactivatingSubscription(false);
    }
  };

  // Handle subscription upgrade
  const handleUpgradeSubscription = async (newPriceId) => {
    try {
      setUpgradingSubscription(true);
      const result = await userService.upgradeSubscription(currentUser.uid, newPriceId);
      
      // Update user data with new subscription status and tier
      setUserData(prev => ({
        ...prev,
        subscription_status: result.subscription_status,
        tier: result.tier
      }));

      toast.success(result.message);
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error(error.message);
    } finally {
      setUpgradingSubscription(false);
    }
  };

  // Get subscription status display
  const getSubscriptionStatusDisplay = () => {
    const status = userData?.subscription_status;
    const tier = userData?.tier;
    
    if (!status || status === 'free') {
      return { text: 'Free Plan', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
    
    switch (status) {
      case 'active':
        return { text: `${tier} Plan`, color: 'text-green-600', bg: 'bg-green-100' };
      case 'cancelling':
        return { text: `${tier} Plan (Cancelling)`, color: 'text-orange-600', bg: 'bg-orange-100' };
      case 'past_due':
        return { text: 'Payment Overdue', color: 'text-red-600', bg: 'bg-red-100' };
      case 'unpaid':
        return { text: 'Payment Failed', color: 'text-red-600', bg: 'bg-red-100' };
      case 'cancelled':
        return { text: 'Cancelled', color: 'text-gray-600', bg: 'bg-gray-100' };
      default:
        return { text: status, color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  // Check if user can cancel subscription
  const canCancelSubscription = () => {
    const currentTier = userData?.tier || 'free';
    return userData?.subscription_status === 'active' && 
           userData?.subscription_id && 
           currentTier !== 'free' && 
           currentTier !== 'starter';
  };

  // Check if user can reactivate subscription
  const canReactivateSubscription = () => {
    const currentTier = userData?.tier || 'free';
    return userData?.subscription_status === 'cancelling' && 
           userData?.subscription_id && 
           currentTier !== 'free' && 
           currentTier !== 'starter';
  };

  // Get available upgrade options based on current tier
  const getUpgradeOptions = () => {
    const currentTier = userData?.tier || 'free';
    const options = [];
    
    if (currentTier === 'free' || currentTier === 'starter') {
      options.push({
        priceId: 'price_1RhJnBHajCWqQSXMlSqZ8kWf',
        tier: 'Pro',
        description: 'Upgrade to Pro'
      });
      options.push({
        priceId: 'price_1RhJnOHajCWqQSXMgO0sdGoX',
        tier: 'Premium',
        description: 'Upgrade to Premium'
      });
    } else if (currentTier === 'Pro') {
      options.push({
        priceId: 'price_1RhJnOHajCWqQSXMgO0sdGoX',
        tier: 'Premium',
        description: 'Upgrade to Premium'
      });
    }
    
    return options;
  };

  const renderLoadingState = () => (
    <div className="flex min-h-[calc(100vh-80px)] items-center justify-center">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <motion.img
          src="/logo.png"
          alt="Quis Lab Logo"
          className="h-16 w-16 mx-auto mb-4"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
        <p className="mt-4 text-gray-600">Loading your dashboard...</p>
      </motion.div>
    </div>
  );

  const renderErrorState = () => {
    if (error === 'session_expired') {
      return (
        <div className="flex min-h-[calc(100vh-80px)] items-center justify-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="text-red-600 mb-4">Your session has expired. Please log in again.</p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleLogout}
              className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors shadow-md"
            >
              Log in again
            </motion.button>
          </motion.div>
        </div>
      );
    }
    return (
      <div className="flex min-h-[calc(100vh-80px)] items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <p className="text-red-600">Your session has expired. Please log in again.</p>
        </motion.div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return renderLoadingState();
    if (error) return renderErrorState();
    if (!userData) return renderErrorState();

    // Calculate percentages and stats from real data
    const invitedCount = userData.referral_count || 0;
    const inviteGoal = userData.goal || 10;
    const invitePercentage = Math.round((invitedCount / inviteGoal) * 100);

    // Calculate task statistics
    const completedTasks = tasks.filter(task => task.completed).length;
    const totalTasks = tasks.length;
    const taskPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const allTasksCompleted = completedTasks === totalTasks && totalTasks > 0;

    return (
      <div className="container mx-auto max-w-5xl px-4">
        {/* Tab Navigation */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white/60 backdrop-blur-md rounded-xl p-1 border border-gray-200/50">
            <div className="flex space-x-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab('profile')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === 'profile'
                    ? 'bg-black text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>User Profile</span>
                </div>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab('referral')}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  activeTab === 'referral'
                    ? 'bg-black text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>Referral Program</span>
                </div>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'referral' && (
            <motion.div
              key="referral"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className="mb-8">
                <h2 className="text-3xl font-bold mb-6">Your Referral Progress</h2>

                <div className="bg-white/60 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-gray-200/50">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    {/* Left column - Invited People Progress */}
                    <div className="md:col-span-3 flex flex-col gap-6">
                      {/* Circular progress bar for invites */}
                      <div className="flex flex-col items-center">
                        <div className="text-sm font-medium text-gray-500 mb-2 flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          People Invited
                        </div>
                        <div className="relative aspect-square flex items-center justify-center w-40 h-40">
                          {" "}
                          {/* Added w-40 h-40 for explicit sizing */}
                          <CircularProgressBar
                            value={invitePercentage}
                            color="#000000"
                          />
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-bold">
                              {invitedCount}
                            </span>
                            <span className="text-xs text-gray-500">
                              of {inviteGoal} goal
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Referral code box */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="text-sm font-medium mb-2">
                          Your Referral Code
                        </div>
                        <div className="flex items-center justify-between bg-white border border-gray-200 rounded p-2">
                          <code className="text-sm">{userData.referral_code}</code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(userData.referral_code);
                              toast('Copied to clipboard');
                            }}
                            className="text-xs text-gray-500 hover:text-black"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Center content */}
                    <div className="md:col-span-8 border border-gray-200 rounded-lg p-6 flex flex-col">
                      <div className="flex-1 flex flex-col">
                        <div className="mb-auto">
                          <h3 className="font-medium mb-4">
                            Referral Program Steps
                          </h3>
                          <ol className="space-y-4 text-gray-600 mb-6">
                            <li className="flex items-start">
                              <span className="font-medium mr-2">1.</span>
                              <span>
                                Share your unique referral code with friends and
                                colleagues
                              </span>
                            </li>
                            <li className="flex items-start">
                              <span className="font-medium mr-2">2.</span>
                              <span>
                                When they sign up using your code, you'll earn
                                points
                              </span>
                            </li>
                            <li className="flex items-start">
                              <span className="font-medium mr-2">3.</span>
                              <span>
                                Reach your invitation goal to unlock special rewards
                              </span>
                            </li>
                          </ol>

                          {/* Claim Reward Button - Only visible when all tasks are completed */}
                          {allTasksCompleted && (
                            <div className="mt-4 flex justify-center">
                              {/* Button with black gradient */}
                              <button
                                onClick={() => setShowGiftModal(true)}
                                className="relative bg-black hover:bg-gray-800 text-white py-2 px-5 rounded-md transition-all duration-300 group overflow-hidden shadow-sm"
                              >
                                {/* Shine effect on hover */}
                                <div className="absolute inset-0 w-1/4 h-full bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-20deg] translate-x-[-150%] group-hover:translate-x-[200%] transition-all duration-1000 ease-in-out"></div>

                                <div className="flex items-center justify-center space-x-1.5">
                                  <Sparkles className="w-3.5 h-3.5 text-gray-300" />
                                  <span className="font-medium text-sm">
                                    Claim Reward
                                  </span>
                                  <Gift className="w-3.5 h-3.5 text-gray-300" />
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right progress bar - Task Completion */}
                    <div className="md:col-span-1 flex flex-col items-center">
                      <div className="text-xs font-medium text-gray-500 mb-2 text-center">
                        <div className="flex items-center justify-center mb-1">
                          <CheckCircle className="w-4 h-4 mr-1" />
                        </div>
                        Tasks
                      </div>
                      <div className="h-full min-h-[200px] flex justify-center">
                        <VerticalProgressBar
                          value={taskPercentage}
                          color="#000000"
                          completedTasks={completedTasks}
                          totalTasks={totalTasks}
                          onStarClick={() => setShowGiftModal(true)} // Assuming VerticalProgressBar can handle this prop
                        />
                      </div>
                      <div className="text-xs font-medium mt-2">
                        {completedTasks}/{totalTasks}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="mb-12">
                <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-gray-200/50">
                  <h3 className="font-medium mb-4">Current Stats</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Users className="w-5 h-5 mr-2 text-gray-700" />
                        <div className="text-sm font-medium text-gray-700">
                          Referrals
                        </div>
                      </div>
                      <div className="text-2xl font-bold">{invitedCount}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        people invited
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Award className="w-5 h-5 mr-2 text-gray-700" />
                        <div className="text-sm font-medium text-gray-700">
                          Points
                        </div>
                      </div>
                      <div className="text-2xl font-bold">{userData.score}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        total earned
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <CheckCircle className="w-5 h-5 mr-2 text-gray-700" />
                        <div className="text-sm font-medium text-gray-700">
                          Tasks
                        </div>
                      </div>
                      <div className="text-2xl font-bold">
                        {completedTasks}/{totalTasks}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">completed</div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <TrendingUp className="w-5 h-5 mr-2 text-gray-700" />
                        <div className="text-sm font-medium text-gray-700">
                          Progress
                        </div>
                      </div>
                      <div className="text-2xl font-bold">{invitePercentage}%</div>
                      <div className="text-xs text-gray-500 mt-1">to goal</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task list section */}
              {renderTaskList()}
            </motion.div>
          )}

          {activeTab === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="bg-white/60 backdrop-blur-md rounded-2xl p-8 shadow-xl border border-gray-200/50"
            >
              <h2 className="text-3xl font-bold mb-6">User Profile</h2>
              <div className="space-y-6">
                {/* Profile Header */}
                <div className="flex items-center space-x-4">
                  {userData.profile_picture ? (
                    <img 
                      src={userData.profile_picture} 
                      alt="Profile" 
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gray-200 rounded-full"></div>
                  )}
                  <div>
                    <h3 className="text-xl font-medium">{userData.full_name || 'No name set'}</h3>
                    <p className="text-gray-500">{userData.email}</p>
                  </div>
                </div>

                {/* Profile Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Read-only Information */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Full Name</label>
                      <input
                        type="text"
                        className="mt-1 block w-full p-2 rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={userData.full_name || ''}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        className="mt-1 block w-full p-2 rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                        value={userData.email}
                        disabled
                      />
                    </div>
                  </div>

                  {/* Editable Bio */}
                  <div>
                    <form onSubmit={handleProfileUpdate} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">About Me</label>
                        <textarea
                          name="bio"
                          className="mt-1 block w-full rounded-md p-2 border-gray-300 shadow-sm focus:border-black focus:ring-black sm:text-sm"
                          rows="4"
                          defaultValue={userData.bio || ''}
                          placeholder="Tell us about yourself... Add links to your social media, websites, etc."
                        />
                      </div>
                      <div className="flex justify-end">
                        <button 
                          type="submit"
                          disabled={updatingProfile}
                          className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {updatingProfile ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Updating...
                            </>
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Subscription Management Section */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium mb-4 flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Subscription Management
                  </h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">Current Plan:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubscriptionStatusDisplay().bg} ${getSubscriptionStatusDisplay().color}`}>
                            {getSubscriptionStatusDisplay().text}
                          </span>
                        </div>
                        {userData.subscription_id && (
                          <p className="text-xs text-gray-500 mt-1">
                            Subscription ID: {userData.subscription_id}
                          </p>
                        )}
                      </div>
                      
                      {/* Cancel Subscription Button - Moved to right corner */}
                      {canCancelSubscription() && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setShowCancelConfirm(true)}
                          disabled={cancellingSubscription}
                          className="bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-sm"
                        >
                          <X className="w-3.5 h-3.5 mr-1.5" />
                          {cancellingSubscription ? 'Cancelling...' : 'Cancel'}
                        </motion.button>
                      )}
                    </div>

                    {/* Other Subscription Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                      {/* Reactivate Subscription Button */}
                      {canReactivateSubscription() && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleReactivateSubscription}
                          disabled={reactivatingSubscription}
                          className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          {reactivatingSubscription ? 'Reactivating...' : 'Reactivate Subscription'}
                        </motion.button>
                      )}

                      {/* Upgrade Buttons for Active Subscriptions */}
                      {userData.subscription_status === 'active' && 
                       userData.tier !== 'free' && 
                       userData.tier !== 'starter' && 
                       getUpgradeOptions().length > 0 && (
                        <>
                          {getUpgradeOptions().map((option) => (
                            <motion.button
                              key={option.priceId}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handleUpgradeSubscription(option.priceId)}
                              disabled={upgradingSubscription}
                              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                              <TrendingUp className="w-4 h-4 mr-2" />
                              {upgradingSubscription ? 'Upgrading...' : option.description}
                            </motion.button>
                          ))}
                        </>
                      )}

                      {/* Upgrade Button for Free Users */}
                      {(!userData.subscription_status || userData.subscription_status === 'free' || userData.subscription_status === 'starter') && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setShowPricingModal(true)}
                          className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors flex items-center"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Upgrade Plan
                        </motion.button>
                      )}

                      {/* Payment Issues Warning */}
                      {(userData.subscription_status === 'past_due' || userData.subscription_status === 'unpaid') && (
                        <div className="flex items-center text-orange-600 bg-orange-50 px-3 py-2 rounded-md">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          <span className="text-sm">Payment issue detected. Please update your payment method.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  // Update the task list section
  const renderTaskList = () => (
    <div className="mb-12">
      <h2 className="text-2xl font-bold mb-6">Your Tasks</h2>

      <div className="bg-white/60 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
        {tasks.length === 0 ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-500">Loading tasks...</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200/50">
            {tasks.map((task, index) => (
              <motion.li
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-4 flex items-center hover:bg-white/50 transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center mr-3">
                  <CheckCircle className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{task.title}</p>
                  <p className="text-sm text-gray-500">{task.description}</p>
                  {task.required_referrals > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      Requires {task.required_referrals} referrals
                    </p>
                  )}
                </div>
                {task.completed ? (
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    Completed
                  </span>
                ) : (
                  <button
                    onClick={() => handleCompleteTask(task.id)}
                    disabled={completingTaskId === task.id}
                    className="text-xs bg-black text-white px-3 py-1 rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center min-w-[80px] justify-center"
                  >
                    {completingTaskId === task.id ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                        <span>Completing...</span>
                      </>
                    ) : (
                      'Complete'
                    )}
                  </button>
                )}
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative min-h-screen overflow-hidden ${MAIN_BG_GRADIENT}`}>
      {/* Mouse glow effect */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div id="mouse-glow" className="absolute w-72 h-72 bg-white/30 rounded-full blur-3xl -z-10 transition-transform"></div>
      </div>

      <AppBar 
        showUpgradeButton={true}
        showPricingModal={showPricingModal}
        setShowPricingModal={setShowPricingModal}
        userData={userData}
      />

      {/* Main content */}
      <main className="relative z-10 flex-1 py-12">
        {renderContent()}
      </main>

      {/* Modals */}
      <GiftModal isOpen={showGiftModal} onClose={() => setShowGiftModal(false)} />
      <PricingModal
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />
      
      {/* Cancellation Confirmation Modal */}
      <AnimatePresence>
        {showCancelConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCancelConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                  <X className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-medium">Cancel Subscription</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to cancel your subscription? Your access will continue until the end of your current billing period.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 text-xs">ℹ</span>
                  </div>
                  <div>
                    <div className="font-medium text-blue-800">Cancel at period end</div>
                    <div className="text-sm text-blue-600">
                      You'll keep access to all premium features until your current billing period ends. No charges will be made after cancellation.
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Keep Subscription
                </button>
                <button
                  onClick={() => handleCancelSubscription(true)}
                  disabled={cancellingSubscription}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {cancellingSubscription ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Cancelling...
                    </>
                  ) : (
                    'Cancel Subscription'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <Toaster position="bottom-center" />
    </div>
  );
}